import Banner from '@v2/shared/components/Banner';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import CategoryBand from '@v2/shared/components/CategoryBand';
import Link from '@v2/shared/components/Link';
import ProductBand from '@v2/shared/components/ProductBand';
import Section from '@v2/shared/components/Section';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import { Banner as BannerType, Category } from '@v2/shared/lib/types';

/** A band category, with the "View All" destination its source resolved to. */
export type GiftsBand = Category & {
  view_all_link?: string | null;
};

export interface Props {
  categories: GiftsBand[];
  featuredCategories: Category[];
  moreCategories: Category[];
  /** The bestsellers listing for the whole gifts tree, behind "All Gifts & More". */
  allGiftsLink: string;
  banners: BannerType[];
}

/**
 * The Gifts & More landing page, the merchandised home of what the catalogue still
 * calls miscellaneous goods.
 *
 * Three different category lists arrive, from two different parents, and mixing them up
 * produces a page that looks plausible and is wrong — see GiftsPagePropsBuilder, which
 * explains the split:
 *
 *   categories          the children of gifts-homepage; these are the product bands.
 *   featuredCategories  the featured children of the gifts tree; these are the tiles.
 *   moreCategories      the rest of that tree's visible children, as text links.
 *
 * The hero is a banner rather than markup. Its eyebrow, headline, collage and strip are
 * baked into the asset, so there is nothing here to keep in step with it and a
 * merchandiser can reshoot the whole thing without a deploy. The banners arrive as
 * props rather than being fetched on mount, as the books page's do, because they are
 * the first thing on the page: fetching client-side would leave the top empty on the
 * first paint and shift everything below once they landed.
 *
 * Baking the headline into the banner also bakes it out of the markup, so the page's
 * H1 is text of its own. It is visually hidden rather than drawn: the banner already
 * says "Gifts & More" in its own type directly beneath, and a second headline over it
 * would print the words twice.
 */
export default function Gifts({ categories, featuredCategories, moreCategories, allGiftsLink, banners }: Props) {
  const { buildUrl } = useUrlGenerator();

  // The box, its measure and its spacing are all Layouts.Full's. This page has nothing
  // to say about any of them.
  return (
    <>
      <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Gifts & More' }]} />

      <h1 className="sr-only">Gifts &amp; More</h1>

      {banners.length > 0 && (
        <Section>
          {banners.map((banner, key) => (
            <Banner key={key} banner={banner} />
          ))}
        </Section>
      )}

      <CategoryBand
        title="Shop by Category"
        categories={featuredCategories}
        variant="image"
        link={{ href: buildUrl(allGiftsLink), label: 'All Gifts & More' }}
        footer={
          moreCategories.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 text-sm text-slate-600">
              <span>More:</span>
              {moreCategories.map((category) => (
                <Link
                  key={category.id}
                  href={buildUrl(category.href)}
                  className="font-semibold text-primary hover:underline"
                >
                  {category.name}
                </Link>
              ))}
            </div>
          )
        }
      />

      {categories.map((category) => (
        <ProductBand
          key={category.id}
          title={category.name}
          products={category.products ?? []}
          viewAllLink={category.view_all_link ? buildUrl(category.view_all_link) : undefined}
        />
      ))}
    </>
  );
}

Gifts.layoutName = 'Layouts.Full';
